/**
 * Opening reconstruction quiz: name + developed-piece squares.
 * Player plays the line on the board, then reveals.
 */
import { Chess } from 'chess.js';
import type { Color, Role, Square } from '@domain/chess/types';
import { START_FEN } from '@domain/chess/types';
import { piecesFromFen, squareShade } from '@domain/chess/fen';
import {
  applyMoveLabel,
  castleRook,
  lastMoveTurnsFromSans,
  turnNumber,
} from '@domain/chess/moveTurns';
import type { Opening } from './openings';

export interface PieceStep {
  turn: number;
  square: Square;
  shade: 'light' | 'dark';
}

export interface DevelopedPiece {
  color: Color;
  role: Role;
  square: Square;
  shade: 'light' | 'dark';
  /** Newest full-move number (usual label). */
  turn?: number;
  /** Newest first; older turns are scrolled under `turn`. */
  turns: number[];
  /** Each move this piece made, oldest first — square after that turn. */
  path: PieceStep[];
  /** Taken on this square during the line. */
  captured?: boolean;
}

export interface OpeningQuizItem {
  opening: Opening;
  pieces: DevelopedPiece[];
}

const START_KEYS: ReadonlySet<string> = new Set(
  [
    ['w', 'r', 'a1'],
    ['w', 'n', 'b1'],
    ['w', 'b', 'c1'],
    ['w', 'q', 'd1'],
    ['w', 'k', 'e1'],
    ['w', 'b', 'f1'],
    ['w', 'n', 'g1'],
    ['w', 'r', 'h1'],
    ['w', 'p', 'a2'],
    ['w', 'p', 'b2'],
    ['w', 'p', 'c2'],
    ['w', 'p', 'd2'],
    ['w', 'p', 'e2'],
    ['w', 'p', 'f2'],
    ['w', 'p', 'g2'],
    ['w', 'p', 'h2'],
    ['b', 'r', 'a8'],
    ['b', 'n', 'b8'],
    ['b', 'b', 'c8'],
    ['b', 'q', 'd8'],
    ['b', 'k', 'e8'],
    ['b', 'b', 'f8'],
    ['b', 'n', 'g8'],
    ['b', 'r', 'h8'],
    ['b', 'p', 'a7'],
    ['b', 'p', 'b7'],
    ['b', 'p', 'c7'],
    ['b', 'p', 'd7'],
    ['b', 'p', 'e7'],
    ['b', 'p', 'f7'],
    ['b', 'p', 'g7'],
    ['b', 'p', 'h7'],
  ].map(([color, role, square]) => `${color}:${role}:${square}`),
);

function isStartingPiece(color: Color, role: Role, square: Square): boolean {
  return START_KEYS.has(`${color}:${role}:${square}`);
}

function capturedPiecesFromSans(
  moves: string,
  startFen: string = START_FEN,
): DevelopedPiece[] {
  const game = new Chess(startFen);
  const labels = new Map<Square, number[]>();
  const out: DevelopedPiece[] = [];
  const sans = moves.trim().split(/\s+/).filter(Boolean);
  sans.forEach((san, i) => {
    let m;
    try {
      m = game.move(san);
    } catch {
      return;
    }
    if (!m) return;
    const turn = turnNumber(startFen, i);
    if (m.captured) {
      const square = (
        m.flags.includes('e') ? `${m.to[0]}${m.from[1]}` : m.to
      ) as Square;
      const stack = labels.get(square) ?? [];
      const shade = squareShade(square);
      out.push({
        color: m.color === 'w' ? 'b' : 'w',
        role: m.captured as Role,
        square,
        shade,
        turn: stack[0],
        turns: [...stack],
        path: [{ turn: stack[0] ?? turn, square, shade }],
        captured: true,
      });
    }
    applyMoveLabel(labels, m, turn);
  });
  return out;
}

function stepAt(turn: number, square: Square): PieceStep {
  return { turn, square, shade: squareShade(square) };
}

/** Square this piece sat on after each of its moves (oldest first). */
function piecePathsFromSans(
  moves: string,
  startFen: string = START_FEN,
): Map<Square, PieceStep[]> {
  const game = new Chess(startFen);
  const board = new Map<Square, PieceStep[]>();
  for (const p of piecesFromFen(startFen)) {
    board.set(p.square, []);
  }
  const sans = moves.trim().split(/\s+/).filter(Boolean);
  sans.forEach((san, i) => {
    let m;
    try {
      m = game.move(san);
    } catch {
      return;
    }
    if (!m) return;
    const turn = turnNumber(startFen, i);
    const from = m.from as Square;
    const to = m.to as Square;
    if (m.captured) {
      const capSq = (
        m.flags.includes('e') ? `${m.to[0]}${m.from[1]}` : m.to
      ) as Square;
      board.delete(capSq);
    }
    const path = board.get(from) ?? [];
    board.delete(from);
    path.push(stepAt(turn, to));
    board.set(to, path);
    if (m.flags.includes('k') || m.flags.includes('q')) {
      const rook = castleRook(from, to);
      if (rook) {
        const rookPath = board.get(rook.from) ?? [];
        board.delete(rook.from);
        rookPath.push(stepAt(turn, rook.to));
        board.set(rook.to, rookPath);
      }
    }
  });
  return board;
}

/** Pieces that have left their starting squares, plus pieces taken in the line. */
export function developedPieces(fen: string, moves?: string): DevelopedPiece[] {
  const turns = moves ? lastMoveTurnsFromSans(moves) : new Map<Square, number[]>();
  const paths = moves ? piecePathsFromSans(moves) : new Map<Square, PieceStep[]>();
  const live = piecesFromFen(fen)
    .filter((p) => !isStartingPiece(p.color, p.role, p.square))
    .map((p) => {
      const stack = turns.get(p.square) ?? [];
      const shade = squareShade(p.square);
      const path =
        paths.get(p.square) ??
        (stack[0] !== undefined ? [stepAt(stack[0], p.square)] : [stepAt(1, p.square)]);
      return {
        color: p.color,
        role: p.role,
        square: p.square,
        shade,
        turn: stack[0],
        turns: stack,
        path,
      };
    });
  const taken = moves ? capturedPiecesFromSans(moves) : [];
  return [...live, ...taken].sort(compareDevelopedByTurn);
}

/** Earlier turns first; missing turn last; square as a tie-breaker. */
export function compareDevelopedByTurn(a: DevelopedPiece, b: DevelopedPiece): number {
  const ta = a.turn ?? Number.POSITIVE_INFINITY;
  const tb = b.turn ?? Number.POSITIVE_INFINITY;
  if (ta !== tb) return ta - tb;
  return a.square.localeCompare(b.square);
}

/** Board + turn + castling + ep — ignore half/full move clocks. */
export function fenKey(fen: string): string {
  return fen.trim().split(/\s+/).slice(0, 4).join(' ');
}

export function positionsMatch(a: string, b: string): boolean {
  return fenKey(a) === fenKey(b);
}

export function nextOpeningQuiz(
  openings: readonly Opening[],
  opts?: { rng?: () => number; excludeName?: string },
): OpeningQuizItem {
  const rng = opts?.rng ?? Math.random;
  let pool = openings;
  if (opts?.excludeName) {
    const filtered = openings.filter((o) => o.name !== opts.excludeName);
    if (filtered.length > 0) pool = filtered;
  }
  if (pool.length === 0) {
    throw new Error('No openings for quiz');
  }
  const index = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
  const opening = pool[index]!;
  return { opening, pieces: developedPieces(opening.fen, opening.moves) };
}
